import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { SERVICE_DOWN } from 'common/constants/valuations';
import { capitalizeWords, Logger } from 'utilities';

const useCapIQServiceHealthCheck = (endpointUrl: string, okValue: string) => {
  const [healthCheckValue, setHealthCheckValue] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isMountedRef = useRef(true);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchHealthCheckValue = async () => {
      try {
        const response = await axios.get(endpointUrl, { signal });
        
        // Check if component is still mounted before updating state
        if (!isMountedRef.current) return;
        
        const capIqResponseData = response.data.CapIQServiceHealthCheckBackend;
        const isOk = capIqResponseData === okValue;

        setHealthCheckValue(isOk ? capitalizeWords(capIqResponseData) : SERVICE_DOWN);
        setIsLoading(false);
      } catch (error: any) {
        // Check if it's an abort error first
        if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || error.message?.includes('canceled')) {
          Logger.info(`Request was aborted: ${error.message}`);
          return;
        }
        
        // Check if component is still mounted before updating state
        if (!isMountedRef.current){
          return;
        }
        
        const isOk = error?.response?.data?.CapIQServiceHealthCheckBackend === okValue;

        if (!isOk) {
          setHealthCheckValue(SERVICE_DOWN);
          Logger.error(`There was an error fetching the CapIQ service health check: ${error}`, error);
          setIsLoading(false);
        }
      }
    };

    fetchHealthCheckValue();

    return () => {
      isMountedRef.current = false;
      controller.abort();
    };
  }, [endpointUrl, okValue]);

  return { healthCheckValue, isLoading };
};

export default useCapIQServiceHealthCheck;
